import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useMemo, useState } from 'react';
import { useOrderPreparation } from './useOrderPreparation';

/**
 * Hook centralizado para la gestion de estado y logica de OrderPreparation.
 * Maneja filtros, columnas visibles y props compartidas entre vistas.
 */
export const useOrderPreparationMain = (searchTerm?: string, externalFilterConditions?: FilterConditionOutput[]) => {
  // Estado de filtros
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>([]);
  const [sorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [searchFilter, setSearchFilter] = useState<{ filters: any[]; logic: string } | undefined>(undefined);

  // Columnas visibles (habilita ColumnSelector)
  const DEFAULT_COLUMNS: string[] = ['code', 'location', 'status', 'created_at', 'quantity', 'total_orders', 'type'];
  const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_COLUMNS);

  const handleColumnsChange = useCallback((newSelectedColumns: string[]) => {
    setSelectedColumns(newSelectedColumns);
  }, []);

  // Procesar filtros para la API
  const processedFilters = useMemo(() => {
    try {
      const statusIds: number[] = [];
      const locationIds: number[] = [];
      const courierIds: string[] = [];
      const advancedFilters: Array<{
        column: string;
        operator: string;
        value?: any;
      }> = [];

      // 🎯 Agregar búsqueda como filtro avanzado si existe
      if (searchTerm && searchTerm.trim()) {
        advancedFilters.push({
          column: 'code',
          operator: 'contains',
          value: searchTerm.trim(),
        });
      }

      // 🎯 Usar filtros externos si están disponibles, sino usar los internos
      const conditionsToProcess = externalFilterConditions || filterConditions;

      conditionsToProcess.forEach((condition) => {
        if (condition.column === 'status' && condition.action === 'equals') {
          statusIds.push(Number(condition.value));
        } else if (condition.column === 'location_id' && condition.action === 'in') {
          const parsedIds = (Array.isArray(condition.value) ? condition.value : [condition.value])
            .map((value) => Number(value))
            .filter((value) => !Number.isNaN(value));
          locationIds.push(...parsedIds);
        } else if (condition.column === 'courier_id' && condition.action === 'in') {
          const parsedIds = (Array.isArray(condition.value) ? condition.value : [condition.value])
            .map((value) => String(value))
            .filter(Boolean);
          courierIds.push(...parsedIds);
        } else {
          const requiresArray = ['in', 'not_in'].includes(condition.action);
          const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

          const value = isNullOperator
            ? undefined
            : requiresArray
              ? Array.isArray(condition.value)
                ? condition.value
                : [condition.value]
              : condition.value;

          advancedFilters.push({
            column: condition.column,
            operator: condition.action,
            ...(value !== undefined && { value }),
          });
        }
      });

      return {
        ...(statusIds.length > 0 && { status_ids: statusIds }),
        ...(locationIds.length > 0 && { location_ids: locationIds }),
        ...(courierIds.length > 0 && { courier_ids: courierIds.join(',') }),
        ...(advancedFilters.length > 0 && {
          filters: advancedFilters,
          logic: 'AND',
        }),
        sortBy: (sorting?.field as 'id' | 'created_at' | 'updated_at') ?? 'id',
        sortDirection: sorting?.direction ?? 'desc',
      };
    } catch {
      return {};
    }
  }, [filterConditions, sorting, searchTerm, externalFilterConditions]);

  // Filtros para el hook de datos
  const queryFilters = useMemo(
    () => ({
      searchTerm: searchTerm || '',
      ...processedFilters,
      sorting,
    }),
    [searchTerm, processedFilters, sorting],
  );

  // Datos con infinite scroll
  const {
    orderpreparation,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
  } = useOrderPreparation(queryFilters);

  const handleFilterChange = useCallback((conditions: FilterConditionOutput[]) => {
    setFilterConditions(conditions);
  }, []);

  const handleSearch = useCallback((filterObj: { filters: any[]; logic: string }) => {
    setSearchFilter(filterObj);
  }, []);

  // Props compartidas para desktop/mobile
  const orderpreparationViewProps = useMemo(
    () => ({
      orderpreparation,
      isLoading,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      refetch,
      sorting,
    }),
    [orderpreparation, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, total, refetch, sorting],
  );

  const hasActiveFilters = useMemo(() => {
    return filterConditions.length > 0 || searchFilter !== undefined || sorting !== null;
  }, [filterConditions.length, searchFilter, sorting]);

  return {
    // Estados
    filterConditions,
    sorting,
    selectedColumns,

    // Datos
    orderpreparation,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    orderpreparationViewProps,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    refetch,

    // Infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  };
};
